import React, { useEffect, useCallback } from 'react';
import { useApiQuery } from '@orderx/lab/xr/http';
import { useCustomerOrders } from '../../../hooks/customer-orders';
import { Order, OrderStatus } from '../../../XV2';
import { useInterval } from '../../../hooks/interval';
import { useRouter } from '../../../hooks/router';
import { useOnResume } from '../../../hooks/on-resume';
import { apiOrderOrderIdGet } from '../../../api/order.{orderId}.[get]';
import { XOverlay } from '../../../components/x/overlay/XOverlay';
import { OrderViewDefault } from '../../../components/order/view/default/OrderViewDefault';
import { useActiveOrder } from '../../../hooks/active-order';
import { useCurrency } from '../../../hooks/currency';
import { awsIotSubscription } from '../../../services/aws-iot/subscription';
import { toastShowError } from '@orderx/lab/xr/toast';
import { XRouterRedirect } from '../../../components/x/router/redirect/XRouterRedirect';
import { RootIdPage } from '../../id/RootIdPage';
import { Product, ProductType } from '../../../XV2';
import { OrderView } from '../../../components/order/view/OrderView';
import { ZoneOrderDeliveryType } from '../../../XV2';
import { OrderViewShowCode } from '../../../components/order/view/show-code/OrderViewShowCode';

export type OrderIdPageResult = Order & {
    products: Pick<Product, 'id' | 'name' | 'type'>[];
};

export function OrderIdPage() {
    const {
        params: { orderId },
        query: { linkBack, status },
    } = useRouter();
    const activeOrder = useActiveOrder();
    const customerOrders = useCustomerOrders();
    const { setCurrency } = useCurrency();
    const isVippsFailed = status === '202';

    const response = useApiQuery(async () =>
        apiOrderOrderIdGet({
            params: { orderId },
        }),
    );

    const loadOrder = response.run;
    const result = response.response as OrderIdPageResult;

    const reloadOrder = useCallback(loadOrder, [orderId]);

    useEffect(() => {
        activeOrder.resetActiveOrder();
        customerOrders.setIsBubblesVisible(false);
        return () => customerOrders.setIsBubblesVisible(true);
    }, []);

    awsIotSubscription.useOrderUpdate(orderId, reloadOrder);
    useOnResume(reloadOrder, [reloadOrder]);
    useInterval(reloadOrder, 30000, [reloadOrder]);

    useEffect(() => {
        reloadOrder();
    }, [reloadOrder]);

    useEffect(() => {
        if (result) {
            setCurrency(result.location.currency);

            if (result.status === OrderStatus.OPEN) {
                customerOrders.cacheOrderId(result.id);
                customerOrders.loadOrders();
            }
        }
    }, [result]);

    useEffect(() => {
        if (isVippsFailed) {
            toastShowError('Payment failed');
        }
    }, [isVippsFailed]);

    if (!result) {
        return <XOverlay type="loading" />;
    }

    if (isVippsFailed) {
        return <XRouterRedirect pageOrPath={RootIdPage} params={{ id: result.zone?.hash || result.zoneId }} />;
    }

    if (result.zone?.orderDeliveryType === ZoneOrderDeliveryType.SHOW_CODE) {
        return <OrderViewShowCode result={result} linkBack={linkBack} />;
    }

    const products = result.products || [];
    const hasTicket = Boolean(products.find((product) => product.type === ProductType.TICKET));

    if (hasTicket) {
        return <OrderView result={result} linkBack={linkBack} onUpdate={reloadOrder} />;
    }

    return <OrderViewDefault order={result} linkBack={linkBack} />;
}

OrderIdPage.route = '/order/:orderId';
